<script>
import { mapGetters } from 'vuex';

import { 
    createMap, 
    createMarker, 
    fitMapBounds, 
    createInfoWindow
 } from '../utils/mapUtils';

export default {
    props: {
        zoomControlPosition: {type: Number, default: 3},
        pinSize: {type: Object, default: () => ({width: 21, height: 28})},
        mapIndex: {type: Number, default: 0},
        lotMaps: {type: Array, required: false}
    },
    data: () => ({
        gm: undefined,
        map: undefined,
        infoWindow: undefined,
        residence: undefined,
        markers: [],
        tileFirstLoad: true,
        config: {}
    }),
    computed: {
        ...mapGetters({appData: 'app/getData'}),

        lotMap() {
            const lotMaps = this.appData.lot_maps || this.lotMaps;
            return lotMaps ? lotMaps[this.mapIndex] : null;
        },

        lots() {
            const lotMaps = this.appData.lot_maps || this.lotMaps;
            return lotMaps ? lotMaps[this.mapIndex].mapLots : null;
        },

        legend() {
            return this.appData.legend;
        }
    },
    created() {
        window.appData = this.appData;
        this.config = {
            layerId: 'my_custom_layer',
            icons: {
                'sold': '/images/site/maps/map-pin.svg',
                'model': '/images/site/maps/map-pin.svg',
                'pre-move-in': '/images/site/maps/map-pin.svg',
                'express': '/images/site/maps/map-pin.svg',
                'available-lot': '/images/site/maps/map-pin.svg',
                'available-home': '/images/site/maps/map-pin.svg',
                'future-release': '/images/site/maps/map-pin.svg',
            },
            map: {
                fullScreenControl: false,
                zoomControlOptions: {
                    position: this.zoomControlPosition
                }
            }
        };
    },
    mounted(){
        events.$on("map-intialized", (map) => {
                this.initMap(map);
        });
    },
    methods: {
        async initMap() {
            // const [mapsLib] = await Promise.all([
            //     google.maps.importLibrary('maps')
            // ]);
            this.gm = google.maps;
            this.map = await createMap('siteplanMap', {
                center: {lat: 0, lng: 0},
                zoom: 3,
                streetViewControl: false,
                mapTypeControl: false,
            });
            this.setLotMap();
            this.infoWindow = createInfoWindow({});
            this.initListeners();
            // this.map = new google.maps.Map(document.getElementById("siteplanMap"), {
            //     mapId: 'YOUR_MAP_ID',
            //     zoom: 3,
            //     center: { lat: 0, lng: 0 },
            //     streetViewControl: false,
            //     mapTypeControl: false
            // });

            const latLngBounds = new google.maps.LatLngBounds(
                {lat: -60, lng: -22.5},
                {lat: 60, lng: 22.5}
            );

            this.map.addListener('drag', () => {
                if (latLngBounds.contains(this.map.getCenter())) return;
                
                const center = this.map.getCenter();
                let x = center.lng();
                let y = center.lat();
                
                const bounds = {
                    max_x: latLngBounds.getNorthEast().lng(),
                    max_y: latLngBounds.getNorthEast().lat(),
                    min_x: latLngBounds.getSouthWest().lng(),
                    min_y: latLngBounds.getSouthWest().lat()
                };

                x = Math.min(Math.max(x, bounds.min_x), bounds.max_x);
                y = Math.min(Math.max(y, bounds.min_y), bounds.max_y);

                this.map.setCenter(new google.maps.LatLng(y, x));
            });
           
        },
        initListeners() {
            const idle = this.gm.event.addListener(this.map, 'idle', () => {
                if (this.map.getZoom() > 3) {
                    this.map.setZoom(3);
                }
                this.gm.event.removeListener(idle);
            });
            // Put some limitations on how far the map can be dragged
            const lat_lng_bounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(-60, -72), // Southwest
                new google.maps.LatLng(60, 72) // Northeast
            );

            this.gm.event.addListener(this.map, 'tilesloaded', () => {
                if (this.tileFirstLoad) {
					// special case for Hawkstone
					if (this.lotMap.id === 13) {
						this.map.setZoom(3);
					} else {
						// this.map.setZoom(2);
					}
                    this.tileFirstLoad = false;
                }
            });

            this.gm.event.addListener(this.map, 'zoom_changed', () => {
                this.closeInfoWindows();
            });
        },
        setLotMap() {
            if (this.lotMap) {
                // Create a new ImageMapType layer
                const layer = new this.gm.ImageMapType({
                    name: this.config.layerId,
                    getTileUrl: (coord, zoom) => {
                        const url = this.lotMap.tiles_path
                            .replace('{x}', coord.x)
                            .replace('{y}', coord.y)
                            .replace('{z}', zoom);
                        return url;
                    },
                    tileSize: new this.gm.Size(256, 256),
                    maxZoom: 4,
                    zoom: 3
                });
                this.map.mapTypes.set(this.config.layerId, layer);
                this.map.setMapTypeId(this.config.layerId);
                this.loadLots();
            }
        },
        loadLots() {
            this.lots.forEach((lot) => {
                if (lot.latitude) {
                    this.addLotMarker(lot);
                }
            });
        },
        createInfoWindow() {
            if (!this.infoWindow) {
                this.infoWindow = new this.gm.InfoWindow({});
                google.maps.event.addListener(this.infoWindow, 'domready', () => {
                    const parent = document.querySelector('.gm-style-iw').parentNode;
                    parent.classList.add('custom-iw');
                    parent.lastChild.classList.add('custom-close-iw');
                    parent.firstChild.classList.add('custom-window-iw');
                });
            }
        },
        closeInfoWindows() {
            if (this.infoWindow) {
                this.infoWindow.close();
            }
        },
        getIconColor(lot) {
            if ( lot.status === 'available-lot' || lot.status === 'available-home' || lot.status === 'pre-move-in' || lot.status === 'express' ) {
                return '#29D029';
            }
            if ( lot.status === 'sold' ) {
                return '#CB2626';
            }
            if ( lot.status === 'model' ) {
                return '#343679';
            }
            if ( lot.status === 'future-release' ) {
                return '#BBB7B6';
            }
        },
        getUrlIcon(lot) {
            if ( lot.status === 'available-lot' || lot.status === 'available-home' || lot.status === 'pre-move-in' || lot.status === 'express' ) {
                return '/images/site/maps/lotmap/lotmap-pin-green.svg';
            }
            if ( lot.status === 'sold' ) {
                return '/images/site/maps/lotmap/lotmap-pin-red.svg';
            }
            if ( lot.status === 'model' ) {
                return '/images/site/maps/lotmap/lotmap-pin-blue.svg';
            }
            if ( lot.status === 'future-release' ) {
                return '/images/site/maps/lotmap/lotmap-pin-gray.svg';
            }
        },
        getIcon(lot) {
            const status = lot.status === 'available-lot' || status === 'available-home' ? 'express' : lot.status;
            return `/images/site/maps/lotmap/${lot.plan.toLowerCase()}--${status}.svg`;
        },
        lotHasInfoWindow(lot) {
            return lot.status === 'pre-move-in' ||
				lot.status === 'express' ||
                lot.status === 'model' ||
                lot.status === 'available-lot' ||
                lot.status === 'available-home';
        },
       async addLotMarker(lot) {
            const iconColor = this.getIconColor(lot);
            // const marker = new this.gm.Marker({
            //     icon: {
            //         path: google.maps.SymbolPath.CIRCLE,
            //         scale: 7.5,
            //         fillColor: iconColor,
            //         fillOpacity: 1,
            //         strokeWeight: 2,
            //         strokeColor: '#fff',
            //         color: '#fff',
            //     },
            //     label: {
            //         color: '#fff',
            //         text: lot.marker_label,
            //         fontWeight: 'bold',
            //     },
            //     position: {
            //         lat: parseFloat(lot.latitude),
            //         lng: parseFloat(lot.longitude)
            //     },
            //     clickable: this.lotHasInfoWindow(lot),
            //     map: this.map,
            //     optimized: false,
            //     content: lot
            // });

            const marker = await createMarker({
                map: this.map,
                position: {
                    lat: parseFloat(lot.latitude),
                    lng: parseFloat(lot.longitude)
                },
                icon: this.getUrlIcon(lot),
                title: lot.marker_label,
                className: 'custom-marker',
                size: { width: 15, height: 15 },
                id: `marker_${lot.id}`, 
                usePin: false,
            });

            // Get info windows if flyout needed
            if (this.lotHasInfoWindow(lot)) {
                const template = this.formattedTemplate(lot);
                marker.addListener('click', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close();
                        this.infoWindow.setContent(template);
                        this.infoWindow.open(this.map, marker);
                    }
                });
                marker.content.addEventListener('mouseover', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close();
                        this.infoWindow.setContent(template);
                        this.infoWindow.open(this.map, marker);
                    }
                });
                marker.content.addEventListener('mousedown', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close();
                        this.infoWindow.setContent(template);
                        this.infoWindow.open(this.map, marker);
                    }
                });
                this.gm.event.addListener(this.map, 'click', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close();
                    }
                });
            }
            this.markers.push(marker);
            fitMapBounds(this.map, this.markers);
        },
        getElevation(lot) {
            return lot.cover;
        },
        formattedTemplate(lot) {
            // console.log(lot.url);
            const elevation = this.getElevation(lot);
            const description = lot.short_description;
            const price = lot.price;
            const monthly = lot.payment;
            const url = lot.status === 'express' ? lot.url : lot.residence_url;
            const name = lot.residence_name;
            const descriptionTemplate = description ? `<div class="description no-margin">
                <div class="truncated">${description.substring(0, 30)}</div>
                <div class="full">${description}</div>
            </div>` : '';
            const statusTemplate = lot.status === 'model'
                ? `<span class="lot-number">Model Home</span>`
                : `<span class="lot-number">${price}</span>`;
            const paymentTemplate = monthly !== 'TBD'
                ? `<span class="monthly-payment">| ${monthly}/mo*</span>`
                : '';
            return `<div class="community-site-plan-flyout"><div class="content-wrap">
                <section class="site-plan-flyout__elevation col-xs-12 col-sm-4">
                    <div class="framed-image">
                        <div class="framed-image-content" style="background-image: url('${elevation}');">
                            <img src="${elevation}" />
                        </div>
                    </div>
                </section>
                <div class="col-xs-12 col-sm-8 right-column">
                    <p class="community-name no-margin">${name}${lot.has_home ? ' - Lot ' + lot.home_lot : ''}</p>
                    <ul class="site-plan-flyout__summary">
                        <li class="residence-sqft">
                            <span class="icon-sqft"></span>
                            ${lot.sqft_range}
                        </li><li class="residence-bed">
                            <span class="icon-bed"></span>
                            ${lot.beds_range}
                        </li><li class="residence-bath">
                            <span class="icon-bath"></span>
                            ${lot.baths_range}
                        </li>
                    </ul>
                    ${descriptionTemplate}
                </div>
            </div><div class="residence-price no-margin">
                <div>${statusTemplate} ${paymentTemplate}</div>
                ${
                    lot.has_home && lot.status !== 'pre-move-in'
                        ? `<a class="visit-link" href="${url}">View</a>`
                        : ''
                }
            </div></div>`;
        }
    }
};
</script>

<style>
    #siteplanMap {
        width: 100%;
        height: 100%;
        position: absolute;
        z-index: 1;
    }
</style>



// WEBPACK FOOTER //
// resources/components/apps/siteplan-map.vue